import { Modal } from './ui';
import {Tag, Calendar, DollarSign, Percent, Truck, X} from 'lucide-react';
import { useUser } from '../hooks/useUser';
import { ActivePromosModalProps } from '../types';
import { PromoCode } from '../types';

export function ActivePromosModal({ isOpen, onClose }: ActivePromosModalProps) {
  const { promoCodes } = useUser();
  const promoCodesArray = Object.values(promoCodes).filter(promo => promo.active);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPromoIcon = (promoType: string | null) => {
    switch (promoType) {
      case 'PERCENTAGE':
        return <Percent className="w-5 h-5" />;
      case 'DOLLAR':
        return <DollarSign className="w-5 h-5" />;
      case 'FREESHIP':
        return <Truck className="w-5 h-5" />;
      default:
        return <Tag className="w-5 h-5" />;
    }
  };

  const getPromoLabel = (promo: PromoCode) => {
    if (promo.promoType === 'PERCENTAGE' && promo.percent) {
      return `${promo.percent}% OFF`;
    } else if (promo.promoType === 'DOLLAR' && promo.balance) {
      return `$${promo.balance.toFixed(2)} BALANCE`;
    } else if (promo.promoType === 'FREESHIP') {
      return 'FREE SHIPPING';
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Active Promotions" size="lg">
        <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors absolute top-4 right-4"
        >
            <X className="w-5 h-5"/>
        </button>

        <div className="p-6">
        {promoCodesArray.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Promotions</h3>
            <p className="text-gray-500">There are currently no promotions available for your account.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {promoCodesArray.map((promo) => {
              const label = getPromoLabel(promo);
              return (
                <div
                  key={promo.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="text-blue-600">
                        {getPromoIcon(promo.promoType)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {promo.promo || 'Promotion'}
                        </h3>
                        {promo.autoapply && (
                          <span className="text-xs text-green-600 font-medium">
                            Auto-applied
                          </span>
                        )}
                      </div>
                    </div>
                    {label && (
                      <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                        {label}
                      </span>
                    )}
                  </div>

                  {promo.memo && (
                    <p className="text-gray-600 mb-3">{promo.memo}</p>
                  )}

                  <div className="flex items-center text-sm text-gray-500 space-x-4">
                    {promo.startDate && promo.endDate && (
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" aria-hidden="true" />
                        <span>
                          {formatDate(promo.startDate)} - {formatDate(promo.endDate)}
                        </span>
                      </div>
                    )}
                  </div>

                  {(promo.promoCategory || promo.promoItemCode || promo.itemLimit || promo.creditcardonly) && (
                    <details className="mt-3">
                      <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                        Restrictions & Details
                      </summary>
                      <div className="mt-2 text-xs text-gray-500 pl-4 space-y-1">
                        {promo.promoCategory && (
                          <p>• Applies to category: {promo.promoCategory}</p>
                        )}
                        {promo.promoItemCode && (
                          <p>• Applies to item: {promo.promoItemCode}</p>
                        )}
                        {promo.itemLimit && (
                          <p>• Limited to {promo.itemLimit} items</p>
                        )}
                        {promo.creditcardonly && (
                          <p>• Credit card payment required</p>
                        )}
                        {promo.excludeShipping && (
                          <p>• Excludes shipping costs</p>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
